/**
 * Verify Email
 *
 * The landing page for the confirmation link. It confirms on arrival rather
 * than asking for another click: the person already clicked, in their email
 * client, and making them do it twice serves nobody.
 *
 * A spent or expired link is not treated as a failure to apologise for. It
 * usually means the address is already confirmed, so the page says what is
 * true and offers the way forward.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useThemeMode, ForceLightTheme, radius } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { verifyEmail } from '../../services/auth';

function VerifyEmailInner() {
  const { palette } = useThemeMode();
  const { isAuthenticated, markEmailVerified } = useAuth();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';

  const [state, setState] = useState(token ? 'working' : 'missing');
  const [message, setMessage] = useState('');
  // React 18 mounts twice in development; without this the second run would
  // redeem an already-spent token and report a failure for a success.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    verifyEmail(token)
      .then(() => {
        setState('done');
        markEmailVerified();
      })
      .catch((err) => {
        setState('failed');
        setMessage(err?.message || 'That link is no longer valid.');
      });
  }, [token, markEmailVerified]);

  const shell = (icon, tone, title, body, actions) => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: palette.bg }}>
      <Container maxWidth="xs">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{ display: 'block', mb: 3, fontWeight: 700, fontSize: '1.05rem', color: palette.accent, textDecoration: 'none' }}
          >
            Farmgate
          </Typography>
          <Box
            sx={{
              width: 48, height: 48, mb: 2.5, borderRadius: `${radius.sm}px`,
              bgcolor: tone === 'ok' ? palette.accentBg : palette.warningBg,
              color: tone === 'ok' ? palette.accent : palette.warning,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: palette.text }}>
            {title}
          </Typography>
          <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3, lineHeight: 1.6 }}>
            {body}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>{actions}</Box>
        </motion.div>
      </Container>
    </Box>
  );

  const primary = (to, label) => (
    <Button
      component={RouterLink}
      to={to}
      variant="contained"
      disableElevation
      sx={{ bgcolor: palette.accent, textTransform: 'none', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#1C9550' } }}
    >
      {label}
    </Button>
  );

  if (state === 'working') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: palette.bg }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={26} sx={{ color: palette.accent, mb: 2 }} />
          <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem' }}>
            Confirming your email…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (state === 'done') {
    return shell(
      <Icon icon="ph:check-circle" width={26} height={26} />,
      'ok',
      'Email confirmed',
      'Thank you. Your address is confirmed and nothing else is needed.',
      primary(isAuthenticated ? '/app/dashboard' : '/login', isAuthenticated ? 'Go to Farmgate' : 'Sign in')
    );
  }

  if (state === 'missing') {
    return shell(
      <Icon icon="ph:link-simple-break" width={26} height={26} />,
      'warn',
      'This link is incomplete',
      'Open the link straight from your email. If it keeps failing, sign in and ask for a new one from the banner at the top.',
      primary('/login', 'Go to sign in')
    );
  }

  return shell(
    <Icon icon="ph:info" width={26} height={26} />,
    'warn',
    'That link has already been used',
    `${message} If you have already confirmed this address, there is nothing more to do. Otherwise sign in and request a new link.`,
    primary(isAuthenticated ? '/app/dashboard' : '/login', isAuthenticated ? 'Go to Farmgate' : 'Sign in')
  );
}

function VerifyEmail() {
  return (
    <ForceLightTheme>
      <VerifyEmailInner />
    </ForceLightTheme>
  );
}

export default VerifyEmail;
